import React from "react";
import { Card } from "@/components/ui/card";
import { BookOpen, History, FileText, MessageCircleQuestion, Gavel } from "lucide-react";
import { fmtDate } from "@/lib/format";

// Hand-over 3.2: context_notes, history, documents_summary, rfis and decisions written on the bid
// (by a person or by Claude via update_bid). Read-only here; edits go through update_bid.
function Section({ icon: Icon, title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><Icon className="w-3.5 h-3.5 text-[#003E87]" /> {title}</div>
      {children}
    </div>
  );
}

export default function BidContextCard({ bid }) {
  if (!bid) return null;
  const has = bid.context_notes || bid.documents_summary || bid.history?.length || bid.rfis?.length || bid.decisions?.length;
  if (!has) return null;
  return (
    <Card className="p-5 space-y-4" data-testid="bid-context-card">
      <h3 className="font-semibold text-black text-sm">Context &amp; history</h3>
      {bid.context_notes && (
        <Section icon={BookOpen} title="Context" testid="bid-context-notes">
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{bid.context_notes}</p>
        </Section>
      )}
      {bid.history?.length > 0 && (
        <Section icon={History} title="History" testid="bid-context-history">
          <ol className="space-y-1.5">
            {bid.history.map((h, i) => (
              <li key={i} className="text-sm text-slate-700 flex gap-3" data-testid={`bid-history-${i}`}>
                <span className="text-xs text-slate-400 w-24 shrink-0 pt-0.5">{h.date ? fmtDate(h.date) : ""}</span>
                <span className="min-w-0">{h.what}{(h.who || h.source) && <span className="text-xs text-slate-400"> · {[h.who, h.source].filter(Boolean).join(" · ")}</span>}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
      {bid.documents_summary && (
        <Section icon={FileText} title="Documents" testid="bid-context-docs">
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{bid.documents_summary}</p>
        </Section>
      )}
      {bid.rfis?.length > 0 && (
        <Section icon={MessageCircleQuestion} title={`RFIs (${bid.rfis.length})`} testid="bid-context-rfis">
          <div className="space-y-2">
            {bid.rfis.map((r, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`bid-rfi-${i}`}>
                <div className="text-sm text-slate-800 font-medium">{r.question}</div>
                {r.answer && <div className="text-sm text-slate-600 mt-0.5">{r.answer}</div>}
                {r.date && <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(r.date)}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {bid.decisions?.length > 0 && (
        <Section icon={Gavel} title={`Decisions (${bid.decisions.length})`} testid="bid-context-decisions">
          <div className="space-y-2">
            {bid.decisions.map((d, i) => (
              <div key={d.id || i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`bid-decision-${i}`}>
                <div className="text-sm text-slate-800 font-medium">{d.question}</div>
                {d.answer && <div className="text-sm text-slate-600 mt-0.5">{d.answer}</div>}
                <div className="text-[11px] text-slate-400 mt-0.5">{[d.date ? fmtDate(d.date) : null, d.who, d.condition ? `if ${d.condition}` : null, d.id].filter(Boolean).join(" · ")}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </Card>
  );
}
